import React, { useEffect, useState } from "react";
import { NeoButton, NeoInput } from "../../../../../components/Common";
import { toast } from "react-toastify";
import { STRENGTH_EXERCISE_LIBRARY } from "../../domain/strength/exerciseLibrary";
import { MUSCLE_GROUPS, STRENGTH_EQUIPMENT } from "../../domain/strength/constants";
import { useTenant } from "../../../../../hooks/useTenant";
import StrengthExerciseLibraryService from "../../../../../services/strength_exercise_library_service";
import Swal from "sweetalert2";
import ConfirmDialog from "../../../../../components/Common/ConfirmDialog";

const optionHtml = (items = [], selected = "") =>
    items.map(item => `<option value="${item}" ${item === selected ? "selected" : ""}>${item}</option>`).join("");

const multiOptionHtml = (items = [], selectedItems = []) =>
    items.map(item => `<option value="${item}" ${selectedItems.includes(item) ? "selected" : ""}>${item}</option>`).join("");

const showExerciseForm = async (item = {}) => {
    const selectedEquipment = Array.isArray(item.equipment) ? item.equipment : [];
    const selectedSecondary = Array.isArray(item.secondaryMuscles) ? item.secondaryMuscles : [];
    const primaryMuscle = item.primaryMuscles?.[0] || item.category || "";

    return Swal.fire({
        title: item.id ? "Editar Exercício" : "Novo Exercício",
        width: 720,
        html: `
            <div class="row g-3 text-start">
                <div class="col-12">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Nome do exercício</label>
                    <input id="strength-exercise-name" class="form-control" placeholder="Ex: Supino reto com barra" value="${item.name || ""}">
                </div>
                <div class="col-md-6">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Músculo principal</label>
                    <select id="strength-exercise-primary" class="form-control">
                        <option value="">Selecione...</option>
                        ${optionHtml(MUSCLE_GROUPS, primaryMuscle)}
                    </select>
                </div>
                <div class="col-md-6">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Músculos secundários</label>
                    <select id="strength-exercise-secondary" class="form-control" multiple size="5">
                        ${multiOptionHtml(MUSCLE_GROUPS, selectedSecondary)}
                    </select>
                    <small class="text-muted">Use Command/Ctrl para selecionar mais de um.</small>
                </div>
                <div class="col-md-6">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Equipamentos</label>
                    <select id="strength-exercise-equipment" class="form-control" multiple size="5">
                        ${multiOptionHtml(STRENGTH_EQUIPMENT, selectedEquipment)}
                    </select>
                    <small class="text-muted">Selecione um ou mais materiais.</small>
                </div>
                <div class="col-md-6">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Dificuldade</label>
                    <select id="strength-exercise-difficulty" class="form-control">
                        ${optionHtml(["Iniciante", "Intermediário", "Avançado"], item.difficulty || "Iniciante")}
                    </select>
                </div>
                <div class="col-md-6">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Tags</label>
                    <input id="strength-exercise-tags" class="form-control" placeholder="hipertrofia, máquina, seguro" value="${(item.tags || []).join(", ")}">
                </div>
                <div class="col-12">
                    <label class="form-label font-size-12 fw-bold text-muted mb-1">Instruções de execução</label>
                    <textarea id="strength-exercise-instructions" class="form-control" rows="4" placeholder="Descreva a execução correta, ajustes e principais cuidados.">${item.instructions || ""}</textarea>
                </div>
            </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Salvar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#FF5722",
        cancelButtonColor: "#6c757d",
        preConfirm: () => {
            const name = document.getElementById("strength-exercise-name").value.trim();
            const primary = document.getElementById("strength-exercise-primary").value;
            const secondary = Array.from(document.getElementById("strength-exercise-secondary").selectedOptions).map(option => option.value);
            const equipment = Array.from(document.getElementById("strength-exercise-equipment").selectedOptions).map(option => option.value);
            const difficulty = document.getElementById("strength-exercise-difficulty").value;
            const instructions = document.getElementById("strength-exercise-instructions").value.trim();
            const tags = document.getElementById("strength-exercise-tags").value
                .split(",")
                .map(tag => tag.trim())
                .filter(Boolean);

            if (!name) {
                Swal.showValidationMessage("O nome do exercício é obrigatório.");
                return false;
            }

            if (!primary) {
                Swal.showValidationMessage("Selecione o músculo principal.");
                return false;
            }

            return {
                name,
                category: primary,
                exercise: {
                    name,
                    category: primary,
                    primaryMuscles: [primary],
                    secondaryMuscles: secondary,
                    equipment,
                    difficulty,
                    instructions,
                    tags,
                },
            };
        },
    });
};

const StrengthExerciseLibraryPicker = ({ onAddExercise, refreshKey = 0 }) => {
    const { idTenant } = useTenant();
    const [customExercises, setCustomExercises] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");

    const loadCustomExercises = React.useCallback(async (mounted = true) => {
        if (!idTenant) return;
        setLoading(true);
        try {
            const data = await StrengthExerciseLibraryService.getAll(idTenant);
            if (mounted) setCustomExercises(data);
        } catch (error) {
            console.error("Erro ao carregar biblioteca de exercícios:", error);
        } finally {
            if (mounted) setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        let mounted = true;
        loadCustomExercises(mounted);
        return () => {
            mounted = false;
        };
    }, [loadCustomExercises, refreshKey]);

    const items = [
        ...customExercises.map(item => ({ ...item.exercise, id: item.id, source: "tenant" })),
        ...STRENGTH_EXERCISE_LIBRARY.map(item => ({ ...item, source: "system" })),
    ];

    const categories = ["all", ...Array.from(new Set(items.map(item => item.category).filter(Boolean)))];
    
    // Filtro por categoria e pesquisa por texto
    const visibleItems = items.filter(item => {
        const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
        const matchesSearch = !searchQuery || 
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.primaryMuscles || []).some(m => m.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (item.equipment || []).some(e => e.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    const handleCreateExercise = async () => {
        const { value: formValues } = await showExerciseForm();
        if (!formValues) return;

        try {
            await StrengthExerciseLibraryService.save({
                idTenant,
                authUser: null,
                ...formValues,
            });
            await loadCustomExercises(true);
            toast.success("Exercício criado na biblioteca.");
        } catch (error) {
            toast.error(error.message || "Erro ao criar exercício.");
        }
    };

    const handleRename = async (event, item) => {
        event.stopPropagation();

        const { value: formValues } = await showExerciseForm(item);
        if (!formValues) return;

        try {
            await StrengthExerciseLibraryService.update({
                idTenant,
                exerciseId: item.id,
                ...formValues,
            });
            await loadCustomExercises(true);
            toast.success("Exercício atualizado!");
        } catch (error) {
            toast.error(error.message || "Erro ao atualizar exercício.");
        }
    };

    const handleDelete = async (event, item) => {
        event.stopPropagation();
        
        const confirmed = await ConfirmDialog({
            title: "Excluir Exercício?",
            text: `Deseja realmente remover o exercício "${item.name}" da sua biblioteca?`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#dc3545",
        });
        
        if (!confirmed) return;

        try {
            await StrengthExerciseLibraryService.delete({ idTenant, exerciseId: item.id });
            await loadCustomExercises(true);
            toast.success("Exercício removido da biblioteca.");
        } catch (error) {
            toast.error(error.message || "Erro ao remover exercício.");
        }
    };

    return (
        <div className="strength-exercise-library">

            <div className="mb-2">
                <NeoInput
                    type="text"
                    placeholder="Buscar por nome, músculo ou material..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="strength-library-search mb-2"
                />
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <h6 className="mb-0 fw-bold font-size-13 text-dark">Biblioteca</h6>
                        <span className="text-muted font-size-11">Exercícios do sistema e personalizados</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <NeoButton
                            type="button"
                            size="sm"
                            variant="soft-secondary"
                            className="d-flex align-items-center gap-1 fw-semibold border-0"
                            onClick={handleCreateExercise}
                        >
                            <i className="mdi mdi-plus" />
                            Novo
                        </NeoButton>
                        <NeoInput
                            type="select"
                            size="sm"
                            value={categoryFilter}
                            onChange={(event) => setCategoryFilter(event.target.value)}
                            className="swim-set-library-filter"
                        >
                            {categories.map(category => (
                                <option key={category} value={category}>
                                    {category === "all" ? "Músculos (Todos)" : category}
                                </option>
                            ))}
                        </NeoInput>
                        {loading && <i className="mdi mdi-loading mdi-spin text-primary ms-2" />}
                    </div>
                </div>
            </div>

            <div className="swim-set-library-grid">
                {visibleItems.map(item => {
                    const isCustom = item.source === "tenant";
                    const eqText = item.equipment ? item.equipment.join(", ") : "Livre";
                    return (
                        <button
                            key={`${item.source}-${item.id}`}
                            type="button"
                            className="strength-library-item"
                            onClick={() => onAddExercise(item)}
                        >
                            <span className="fw-bold text-dark font-size-13">{item.name}</span>
                            <span className="text-muted font-size-11">
                                {item.category} • {eqText} • {item.source === "tenant" ? "Minha biblioteca" : "Sistema"}
                            </span>
                            {isCustom && (
                                <span className="strength-library-item-actions">
                                    <button type="button" title="Renomear exercício" onClick={(event) => handleRename(event, item)}>
                                        <i className="mdi mdi-pencil-outline" />
                                    </button>
                                    <button type="button" title="Excluir exercício" onClick={(event) => handleDelete(event, item)}>
                                        <i className="mdi mdi-trash-can-outline" />
                                    </button>
                                </span>
                            )}
                        </button>
                    );
                })}
                {visibleItems.length === 0 && (
                    <div className="text-center py-4 bg-light rounded border border-dashed text-muted font-size-12">
                        Nenhum exercício encontrado.
                    </div>
                )}
            </div>
        </div>
    );
};

export default StrengthExerciseLibraryPicker;
